/**
 * Agent A8 — Evidence Auditor (V85-4-7 / gap F6).
 *
 * Reviews a system's obligation set + evidence bindings + disclosure for
 * completeness. Flags:
 *   - Missing evidence: obligations with no evidence binding
 *   - Expired evidence: bindings whose validity has lapsed
 *   - Insufficient coverage: evidence that only partially satisfies
 *   - Disclosure gaps: obligation-required disclosures not present
 *
 * Runs through the V85-4-0 agent harness (`runAgent`). Output writes
 * to the `findings` queue — each finding is a proposal, not an
 * authoritative change (declared approval boundary, ADR-0039).
 *
 * Provenance: every finding cites the specific obligation, evidence
 * binding, or disclosure it references.
 */
import { defineTemplate, field, referenceDerived } from '../egress/data-classes.js';
// ---- Agent definition -------------------------------------------------------
export const A8_DEFINITION = {
    agent_id: 'A8',
    prompt_version: '2026.09.20',
    queue_target: 'findings',
    schema_version: '1.0',
};
/** Output schema for harness validation. */
export const A8_OUTPUT_SCHEMA = {
    type: 'object',
    required: ['system_id', 'summary', 'completeness_score', 'findings'],
    properties: {
        system_id: { type: 'string' },
        summary: { type: 'string' },
        completeness_score: { type: 'number' },
        findings: { type: 'array' },
    },
};
// ---- Prompt -----------------------------------------------------------------
const AUDIT_PROMPT_TEMPLATE = `You are an AI governance evidence auditor. Review the evidence package for an AI system and identify gaps.

## System under audit
Name: {system_name}
System ID: {system_id}
Audit date: {as_of}

## Obligations ({obligation_count} total)
{obligations_section}

## Evidence bindings ({evidence_count} total)
{evidence_section}

## Disclosure status
{disclosure_section}

## Instructions

Review the evidence package for completeness and identify findings:

1. **Missing evidence**: For each obligation that requires evidence, check if any evidence binding references it. If not, it is a "missing_evidence" finding.
2. **Expired evidence**: For each evidence binding with an expires_at date, check if it has expired relative to the audit date ({as_of}). If so, it is an "expired_evidence" finding.
3. **Insufficient coverage**: If evidence exists but its coverage description suggests it does not fully satisfy the obligation, flag it as "insufficient_coverage".
4. **Disclosure gaps**: If the disclosure has required fields that are not present, flag each missing field as a "disclosure_gap".

Severity guidelines:
- critical: No evidence at all for a mandatory obligation
- high: Expired evidence for a mandatory obligation
- medium: Partial coverage or missing disclosure fields
- low: Non-mandatory gaps or minor coverage issues

IMPORTANT:
- Do NOT fabricate evidence references. Only reference evidence_ids and obligation_ids that appear in the input.
- Do NOT invent obligations or evidence bindings that don't exist.
- Base findings strictly on the data provided.

## Output format (JSON)

Return a JSON object with:
- summary: string (one-paragraph audit summary)
- completeness_score: number (0.0-1.0, ratio of fully covered obligations)
- findings: array of { finding_type, severity, title, description, obligation_id?, evidence_id?, recommendation }
`;
// ---- Helpers ----------------------------------------------------------------
function buildAuditPrompt(input) {
    const asOf = input.as_of ?? new Date().toISOString().split('T')[0];
    const obligationsSection = input.obligations.length > 0
        ? input.obligations.map(o => `- [${o.obligation_id}] ${o.obligation_title} (${o.framework}${o.control_ref ? ` / ${o.control_ref}` : ''}) — evidence ${o.evidence_required ? 'REQUIRED' : 'optional'}`).join('\n')
        : 'No obligations defined.';
    const evidenceSection = input.evidence_bindings.length > 0
        ? input.evidence_bindings.map(e => `- [${e.evidence_id}] "${e.title}" (${e.evidence_type}) — covers: ${e.obligation_ids.join(', ')} — collected: ${e.collected_at}${e.expires_at ? ` — expires: ${e.expires_at}` : ''}${e.coverage ? ` — scope: ${e.coverage}` : ''}`).join('\n')
        : 'No evidence bindings.';
    const disclosureSection = input.disclosure
        ? input.disclosure.exists
            ? `Disclosure exists (last updated: ${input.disclosure.last_updated ?? 'unknown'}).
Fields present: ${input.disclosure.fields_present?.join(', ') ?? 'none listed'}
Fields required: ${input.disclosure.fields_required?.join(', ') ?? 'none specified'}`
            : 'No disclosure exists for this system.'
        : 'Disclosure status not provided.';
    return {
        system_name: field('ai_system.description', input.system_name),
        system_id: field('ai_system.description', input.system_id),
        as_of: referenceDerived('iso_date', asOf),
        obligation_count: referenceDerived('count', input.obligations.length),
        obligations_section: field('obligation.system_obligations', obligationsSection),
        evidence_count: referenceDerived('count', input.evidence_bindings.length),
        evidence_section: field('evidence.artifact_text', evidenceSection),
        disclosure_section: field('disclosure.content', disclosureSection),
    };
}
/** ADR-0073: the A8 prompt as a reviewed template (obligations, evidence and disclosure status are tenant data). */
export const A8_TEMPLATE = defineTemplate('agent.a8.evidence_audit', '2026.09.20', AUDIT_PROMPT_TEMPLATE, [
    'system_name', 'system_id', 'as_of', 'obligation_count', 'obligations_section', 'evidence_count', 'evidence_section', 'disclosure_section',
]);
/**
 * Deterministic pre-check: find missing/expired evidence without LLM.
 * The LLM may find additional nuanced findings (insufficient coverage),
 * but these are always caught.
 */
export function deterministicAudit(input) {
    const asOf = input.as_of ?? new Date().toISOString().split('T')[0];
    const findings = [];
    // Map of obligation_id -> evidence_ids that cover it
    const coverageMap = new Map();
    for (const obligation of input.obligations) {
        coverageMap.set(obligation.obligation_id, []);
    }
    for (const evidence of input.evidence_bindings) {
        for (const oblId of evidence.obligation_ids) {
            const existing = coverageMap.get(oblId);
            if (existing) {
                existing.push(evidence.evidence_id);
            }
        }
    }
    // 1. Missing evidence
    for (const obligation of input.obligations) {
        if (!obligation.evidence_required)
            continue;
        const coveringEvidence = coverageMap.get(obligation.obligation_id) ?? [];
        if (coveringEvidence.length === 0) {
            findings.push({
                finding_type: 'missing_evidence',
                severity: 'critical',
                title: `No evidence for obligation: ${obligation.obligation_title}`,
                description: `Obligation ${obligation.obligation_id} (${obligation.framework}${obligation.control_ref ? ` / ${obligation.control_ref}` : ''}) requires evidence but none is bound.`,
                obligation_id: obligation.obligation_id,
                recommendation: `Upload or bind evidence that satisfies ${obligation.obligation_title}.`,
            });
        }
    }
    // 2. Expired evidence
    for (const evidence of input.evidence_bindings) {
        if (!evidence.expires_at)
            continue;
        if (evidence.expires_at < asOf) {
            findings.push({
                finding_type: 'expired_evidence',
                severity: 'high',
                title: `Expired evidence: ${evidence.title}`,
                description: `Evidence ${evidence.evidence_id} ("${evidence.title}") expired on ${evidence.expires_at} (audit date: ${asOf}).`,
                evidence_id: evidence.evidence_id,
                obligation_id: evidence.obligation_ids[0],
                recommendation: `Renew or replace expired evidence "${evidence.title}".`,
            });
        }
    }
    // 3. Disclosure gaps
    if (input.disclosure) {
        if (!input.disclosure.exists) {
            findings.push({
                finding_type: 'disclosure_gap',
                severity: 'high',
                title: 'No disclosure exists',
                description: `System ${input.system_name} has no disclosure document.`,
                recommendation: 'Create an AI disclosure for this system.',
            });
        }
        else if (input.disclosure.fields_required && input.disclosure.fields_present) {
            const presentSet = new Set(input.disclosure.fields_present);
            const missing = input.disclosure.fields_required.filter(f => !presentSet.has(f));
            for (const field of missing) {
                findings.push({
                    finding_type: 'disclosure_gap',
                    severity: 'medium',
                    title: `Disclosure missing field: ${field}`,
                    description: `Required disclosure field "${field}" is not present in the current disclosure.`,
                    recommendation: `Add the "${field}" field to the system disclosure.`,
                });
            }
        }
    }
    return findings;
}
/**
 * Validate findings: ensure they only reference IDs that exist in the input.
 * This is the hard guard against fabricated evidence/obligation references.
 */
export function validateFindings(findings, validObligationIds, validEvidenceIds) {
    return findings.filter(f => {
        // If the finding references an obligation, it must exist
        if (f.obligation_id && !validObligationIds.has(f.obligation_id)) {
            return false;
        }
        // If the finding references evidence, it must exist
        if (f.evidence_id && !validEvidenceIds.has(f.evidence_id)) {
            return false;
        }
        return true;
    });
}
// ---- Agent function ---------------------------------------------------------
/**
 * The A8 evidence auditor agent function. This is passed to `runAgent`.
 */
export const evidenceAuditorAgent = async (llm, input) => {
    const data = input.data;
    if (!data.system_id) {
        throw new Error('A8 evidence auditor: system_id is required');
    }
    if (!data.obligations || !Array.isArray(data.obligations)) {
        throw new Error('A8 evidence auditor: obligations array is required');
    }
    const validObligationIds = new Set(data.obligations.map(o => o.obligation_id));
    const validEvidenceIds = new Set(data.evidence_bindings.map(e => e.evidence_id));
    // Step 1: Deterministic audit (always runs)
    const deterministicFindings = deterministicAudit(data);
    // Step 2: LLM-enhanced audit (finds nuanced issues like insufficient coverage)
    let llmFindings = [];
    let summary = '';
    let completenessScore = 0;
    try {
        let rawOutput;
        // ADR-0073: through the egress gateway; a refusal (no eligible provider for this
        // workspace's data) is the deterministic-only audit (parity agent_a8_evidence_audit).
        const result = await llm.run({
            task: 'agent.a8.evidence_audit', template: A8_TEMPLATE, slots: buildAuditPrompt(data),
            schemaHint: A8_OUTPUT_SCHEMA, options: { temperature: 0.1, maxTokens: 4096 },
        });
        if (result.status === 'ok') {
            rawOutput = result.output;
        }
        else {
            // Fall back to deterministic-only
            rawOutput = {
                summary: `Deterministic audit of ${data.system_name}: ${deterministicFindings.length} finding(s).`,
                completeness_score: deterministicFindings.length === 0 ? 1.0 : 0,
                findings: [],
            };
        }
        summary = rawOutput.summary ?? '';
        completenessScore = Math.max(0, Math.min(1, rawOutput.completeness_score ?? 0));
        // Validate LLM findings (strip any fabricated references)
        llmFindings = validateFindings(rawOutput.findings ?? [], validObligationIds, validEvidenceIds);
    }
    catch {
        // LLM failure: fall back to deterministic findings only
        summary = `Deterministic-only audit of ${data.system_name}: ${deterministicFindings.length} finding(s). LLM analysis unavailable.`;
    }
    // Merge: deterministic findings first (they're authoritative),
    // then LLM findings that don't duplicate
    const deterministicKeys = new Set(deterministicFindings.map(f => `${f.finding_type}:${f.obligation_id ?? ''}:${f.evidence_id ?? ''}`));
    const uniqueLlmFindings = llmFindings.filter(f => {
        const key = `${f.finding_type}:${f.obligation_id ?? ''}:${f.evidence_id ?? ''}`;
        return !deterministicKeys.has(key);
    });
    const allFindings = [...deterministicFindings, ...uniqueLlmFindings];
    // Compute completeness if not from LLM
    if (!completenessScore && data.obligations.length > 0) {
        const requiredObligations = data.obligations.filter(o => o.evidence_required);
        if (requiredObligations.length > 0) {
            const missingCount = allFindings.filter(f => f.finding_type === 'missing_evidence').length;
            completenessScore = Math.max(0, 1 - (missingCount / requiredObligations.length));
        }
        else {
            completenessScore = 1.0;
        }
    }
    if (!summary) {
        summary = `Evidence audit of ${data.system_name}: ${allFindings.length} finding(s), completeness ${(completenessScore * 100).toFixed(0)}%.`;
    }
    // Build provenance map
    const provenance = {
        system_id: {
            source: 'input.system_id',
            span: data.system_id,
            confidence: 1.0,
        },
        summary: {
            source: 'agent.analysis',
            span: summary.slice(0, 200),
            confidence: 0.9,
        },
        completeness_score: {
            source: 'agent.analysis',
            span: `${data.obligations.length} obligations, ${data.evidence_bindings.length} evidence bindings`,
            confidence: 0.85,
        },
        findings: {
            source: 'agent.analysis',
            span: `${allFindings.length} findings (${deterministicFindings.length} deterministic, ${uniqueLlmFindings.length} LLM-enhanced)`,
            confidence: 0.9,
        },
    };
    // Add per-finding provenance
    for (let i = 0; i < allFindings.length; i++) {
        const f = allFindings[i];
        provenance[`finding.${i}`] = {
            source: f.obligation_id
                ? `obligation:${f.obligation_id}`
                : f.evidence_id
                    ? `evidence:${f.evidence_id}`
                    : 'agent.analysis',
            span: f.title,
            confidence: f.finding_type === 'missing_evidence' || f.finding_type === 'expired_evidence' ? 1.0 : 0.8,
        };
    }
    return {
        target: 'findings',
        proposal: {
            system_id: data.system_id,
            summary,
            completeness_score: completenessScore,
            findings: allFindings,
        },
        provenance,
    };
};
//# sourceMappingURL=agent-evidence-auditor-service.js.map